import React, { useState, useEffect, useRef } from 'react';
import { Globe, Plus, ToggleLeft, ToggleRight, AlertCircle, Edit, History, X } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom marker icon
const markerIcon = L.divIcon({
  className: 'custom-map-marker',
  html: `<div style="background-color: var(--primary); border: 2px solid #ffffff; width: 12px; height: 12px; border-radius: 50%; box-shadow: 0 0 8px var(--primary-glow);"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

export default function Zones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneCoords, setNewZoneCoords] = useState('');
  const [zoneStatus, setZoneStatus] = useState('PREPARATION');
  const [formOpen, setFormOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Pricing override state
  const [specificPrice, setSpecificPrice] = useState('');

  // History modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedZone, setSelectedZone] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Map drawing state
  const [draftPoints, setDraftPoints] = useState([]);
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const draftPolylineRef = useRef(null);
  const draftPolygonRef = useRef(null);
  const markersGroupRef = useRef(null);
  const existingZonesGroupRef = useRef(null);

  const fetchZones = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/zones', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur de chargement des zones');
      }

      const data = await response.json();
      setZones(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);

  const handleSubmitZone = async (e) => {
    e.preventDefault();
    if (!newZoneName.trim() || !newZoneCoords.trim()) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingZoneId ? `/api/admin/zones/${editingZoneId}` : '/api/admin/zones';
      const method = editingZoneId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newZoneName,
          polygonCoordinatesJson: newZoneCoords,
          status: zoneStatus,
          specificPrice: specificPrice ? parseFloat(specificPrice) : null,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Erreur lors de la ${editingZoneId ? 'modification' : 'création'} de la zone`;
        throw new Error(errorMessage);
      }

      setNewZoneName('');
      setNewZoneCoords('');
      setZoneStatus('PREPARATION');
      setSpecificPrice('');
      setEditingZoneId(null);
      setFormOpen(false);
      resetDraft();
      await fetchZones();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditZone = (zone) => {
    setEditingZoneId(zone.id);
    setNewZoneName(zone.name);
    setNewZoneCoords(zone.polygonCoordinatesJson);
    setZoneStatus(zone.status);
    setSpecificPrice(zone.specificPrice !== null && zone.specificPrice !== undefined ? zone.specificPrice.toString() : '');
    
    // Parse coordinates and load into Leaflet draft points
    try {
      const coords = JSON.parse(zone.polygonCoordinatesJson);
      const leafletCoords = coords.map(([lng, lat]) => [lat, lng]);
      if (leafletCoords.length > 2) {
        const first = leafletCoords[0];
        const last = leafletCoords[leafletCoords.length - 1];
        if (first[0] === last[0] && first[1] === last[1]) {
          leafletCoords.pop();
        }
      }
      setDraftPoints(leafletCoords);
      setIsPolygonClosed(true);
    } catch (e) {
      console.error("Error parsing zone coordinates for edit", e);
    }
    
    setFormOpen(true);
  };

  const handleShowHistory = async (zone) => {
    setSelectedZone(zone);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/zones/${zone.id}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error("Erreur lors de la récupération de l'historique");
      const data = await response.json();
      setAuditLogs(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleToggleStatus = async (zoneId, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/zones/${zoneId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Impossible de modifier le statut de la zone');
      }

      await fetchZones();
    } catch (err) {
      alert(err.message);
    }
  };

  // Map action helpers
  const closePolygon = () => {
    if (draftPoints.length >= 3) {
      setIsPolygonClosed(true);
    }
  };

  const deleteLastPoint = () => {
    if (isPolygonClosed) {
      setIsPolygonClosed(false);
    }
    setDraftPoints(prev => prev.slice(0, -1));
  };

  const resetDraft = () => {
    setDraftPoints([]);
    setIsPolygonClosed(false);
    if (draftPolylineRef.current) draftPolylineRef.current.setLatLngs([]);
    if (draftPolygonRef.current) draftPolygonRef.current.setLatLngs([]);
    if (markersGroupRef.current) markersGroupRef.current.clearLayers();
  };

  // Draw existing zones
  const drawExistingZones = (map) => {
    if (!existingZonesGroupRef.current) return;
    existingZonesGroupRef.current.clearLayers();

    zones.forEach(zone => {
      try {
        const coords = JSON.parse(zone.polygonCoordinatesJson);
        const leafletCoords = coords.map(([lng, lat]) => [lat, lng]);
        
        const polygon = L.polygon(leafletCoords, {
          color: zone.status === 'ACTIVE' ? 'var(--primary)' : (zone.status === 'PREPARATION' ? 'var(--warning)' : 'var(--text-muted)'),
          fillColor: zone.status === 'ACTIVE' ? 'var(--primary)' : (zone.status === 'PREPARATION' ? 'var(--warning)' : 'var(--text-muted)'),
          fillOpacity: 0.1,
          weight: 1.5,
          opacity: 0.6
        }).addTo(existingZonesGroupRef.current);

        polygon.bindTooltip(zone.name, {
          permanent: false,
          direction: 'center',
          className: 'zone-tooltip'
        });
      } catch (e) {
        console.error("Error parsing coordinates for zone:", zone.name, e);
      }
    });
  };

  // Leaflet Map initialization and cleanup
  useEffect(() => {
    if (!formOpen || !mapRef.current) {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      return;
    }

    // Centered around Paris
    const map = L.map(mapRef.current).setView([48.8566, 2.3522], 11);
    mapInstanceRef.current = map;

    // Dark-themed tiles
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19
    }).addTo(map);

    existingZonesGroupRef.current = L.featureGroup().addTo(map);
    markersGroupRef.current = L.featureGroup().addTo(map);

    draftPolylineRef.current = L.polyline([], {
      color: 'var(--primary)',
      weight: 3,
      dashArray: '5, 8'
    }).addTo(map);

    draftPolygonRef.current = L.polygon([], {
      color: 'var(--success)',
      fillColor: 'var(--success)',
      fillOpacity: 0.2,
      weight: 3
    }).addTo(map);

    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      setDraftPoints(prev => {
        if (isPolygonClosed) return prev;
        return [...prev, [lat, lng]];
      });
    });

    drawExistingZones(map);

    // Try to fit bounds of existing zones if any exist to focus map automatically
    setTimeout(() => {
      if (existingZonesGroupRef.current && existingZonesGroupRef.current.getLayers().length > 0) {
        map.fitBounds(existingZonesGroupRef.current.getBounds(), { padding: [20, 20] });
      }
    }, 100);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen]);

  // Sync draft points with map markers and lines
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (markersGroupRef.current) {
      markersGroupRef.current.clearLayers();
    }

    draftPoints.forEach((point, index) => {
      const marker = L.marker(point, {
        icon: markerIcon,
        draggable: !isPolygonClosed
      }).addTo(markersGroupRef.current);

      // Click first marker to close polygon
      if (index === 0 && draftPoints.length >= 3 && !isPolygonClosed) {
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          setIsPolygonClosed(true);
        });
        marker.bindTooltip("Cliquer ici pour fermer le polygone", { direction: 'top', className: 'close-tooltip' });
      }

      // Update position on drag
      marker.on('dragend', (e) => {
        const newLatLng = e.target.getLatLng();
        setDraftPoints(prev => {
          const next = [...prev];
          next[index] = [newLatLng.lat, newLatLng.lng];
          return next;
        });
      });
    });

    if (isPolygonClosed && draftPoints.length >= 3) {
      if (draftPolylineRef.current) draftPolylineRef.current.setLatLngs([]);
      if (draftPolygonRef.current) {
        draftPolygonRef.current.setLatLngs(draftPoints);
      }
    } else {
      if (draftPolygonRef.current) draftPolygonRef.current.setLatLngs([]);
      if (draftPolylineRef.current) {
        draftPolylineRef.current.setLatLngs(draftPoints);
      }
    }
  }, [draftPoints, isPolygonClosed]);

  // Auto-update zones list overlays when zones are fetched/loaded
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (map && zones.length > 0) {
      drawExistingZones(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones]);

  // Sync draftPoints and closure status to coordinates JSON field
  useEffect(() => {
    if (draftPoints.length === 0) {
      return;
    }

    const geojsonCoords = draftPoints.map(([lat, lng]) => [
      parseFloat(lng.toFixed(6)),
      parseFloat(lat.toFixed(6))
    ]);

    if (isPolygonClosed) {
      const first = geojsonCoords[0];
      const last = geojsonCoords[geojsonCoords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        geojsonCoords.push(first);
      }
    }

    setNewZoneCoords(JSON.stringify(geojsonCoords));
  }, [draftPoints, isPolygonClosed]);

  if (loading) {
    return <div style={styles.loadingContainer}>Chargement des zones géographiques...</div>;
  }

  if (error) {
    return (
      <div style={styles.loadingContainer}>
        <AlertCircle size={24} color="var(--danger)" style={{ marginRight: '8px' }} />
        <span>{error}</span>
      </div>
    );
  }

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PREPARATION': return 'warning';
      default: return 'danger';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return 'Active';
      case 'PREPARATION': return 'Préparation';
      default: return 'Inactive';
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        /* Dark theme Leaflet overrides */
        .leaflet-container {
          background: var(--bg-primary) !important;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border-color);
          font-family: var(--font-body) !important;
        }
        .leaflet-tooltip {
          background: var(--bg-secondary) !important;
          border: 1px solid var(--border-color) !important;
          color: var(--text-primary) !important;
          font-family: var(--font-title) !important;
          font-size: 0.8rem !important;
          border-radius: var(--radius-sm) !important;
          padding: 4px 8px !important;
          box-shadow: var(--shadow-main) !important;
        }
        .leaflet-tooltip-top:before {
          border-top-color: var(--border-color) !important;
        }
        .leaflet-tooltip-center:before {
          border-color: transparent !important;
        }
        .leaflet-bar {
          border: 1px solid var(--border-color) !important;
          box-shadow: var(--shadow-main) !important;
        }
        .leaflet-bar a {
          background-color: var(--bg-secondary) !important;
          color: var(--text-primary) !important;
          border-bottom: 1px solid var(--border-color) !important;
          transition: background-color 0.2s ease;
        }
        .leaflet-bar a:hover {
          background-color: var(--bg-tertiary) !important;
          color: var(--primary) !important;
        }
        .custom-map-marker {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .action-icon-btn {
          background: transparent;
          border: none;
          cursor: pointer;
          color: var(--text-secondary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        .action-icon-btn:hover {
          background-color: var(--bg-tertiary);
          color: var(--primary);
        }
      `}</style>
      <div style={styles.headerRow}>
        <span style={styles.subtitle}>Définissez les secteurs opérationnels de la flotte SAFE</span>
        <button onClick={() => {
          if (formOpen) {
            resetDraft();
            setEditingZoneId(null);
            setNewZoneName('');
            setNewZoneCoords('');
            setZoneStatus('PREPARATION');
            setSpecificPrice('');
          }
          setFormOpen(!formOpen);
        }} style={styles.addBtn} className="btn-primary">
          <Plus size={16} />
          <span>{formOpen ? "Fermer le formulaire" : "Nouvelle Zone"}</span>
        </button>
      </div>

      <div style={styles.mainGrid}>
        {/* Left: Zones list */}
        <div style={styles.tablePanel} className="glass-card">
          {zones.length === 0 ? (
            <div style={styles.emptyTable}>Aucune zone géographique configurée.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Statut</th>
                  <th style={{ textAlign: 'center' }}>Chauffeurs actifs</th>
                  <th style={{ textAlign: 'center' }}>Waitlist</th>
                  <th style={{ textAlign: 'right' }}>CA du mois</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {zones.map((zone) => (
                  <tr key={zone.id}>
                    <td style={styles.zoneName}>{zone.name}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(zone.status)}`}>
                        {getStatusLabel(zone.status)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '500' }}>
                      {zone.activeDriversCount !== undefined ? zone.activeDriversCount : 0}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: '500' }}>
                      {zone.waitlistCount !== undefined ? zone.waitlistCount : 0}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
                      {zone.status === 'ACTIVE'
                        ? `${(zone.monthlyTurnover || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
                        : '-'
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button 
                          onClick={() => handleToggleStatus(zone.id, zone.status)}
                          style={styles.toggleBtn}
                          title={zone.status === 'ACTIVE' ? "Désactiver" : "Activer"}
                        >
                          {zone.status === 'ACTIVE' ? (
                            <ToggleRight size={28} color="var(--success)" />
                          ) : (
                            <ToggleLeft size={28} color="var(--text-muted)" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEditZone(zone)}
                          className="action-icon-btn"
                          title="Modifier"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleShowHistory(zone)}
                          className="action-icon-btn"
                          title="Historique"
                        >
                          <History size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right: Creator / Editor form */}
        {formOpen && (
          <div style={styles.formPanel} className="glass-card">
            <div style={styles.formHeader}>
              <Globe size={18} color="var(--primary)" />
              <h3 style={styles.formTitle}>
                {editingZoneId ? `Modifier la Zone : ${newZoneName}` : "Créer une Zone Opérationnelle"}
              </h3>
            </div>
            <form onSubmit={handleSubmitZone} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Nom de la ville ou du quartier</label>
                <input 
                  type="text" 
                  placeholder="Ex: Paris Centre, Lyon Confluence..." 
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  style={styles.input}
                  className="input-field"
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Statut initial</label>
                <select
                  value={zoneStatus}
                  onChange={(e) => setZoneStatus(e.target.value)}
                  style={styles.select}
                  className="input-field"
                >
                  <option value="PREPARATION">En préparation</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Tracé sur la carte</label>
                <div 
                  ref={mapRef} 
                  style={{ 
                    width: '100%', 
                    height: '240px', 
                    borderRadius: 'var(--radius-sm)', 
                    position: 'relative',
                    overflow: 'hidden'
                  }} 
                />
                <div style={styles.mapControls}>
                  <button
                    type="button"
                    onClick={closePolygon}
                    disabled={draftPoints.length < 3 || isPolygonClosed}
                    style={styles.mapBtn}
                    className="btn-secondary"
                  >
                    Fermer Zone
                  </button>
                  <button
                    type="button"
                    onClick={deleteLastPoint}
                    disabled={draftPoints.length === 0}
                    style={styles.mapBtn}
                    className="btn-secondary"
                  >
                    Retour
                  </button>
                  <button
                    type="button"
                    onClick={resetDraft}
                    disabled={draftPoints.length === 0}
                    style={styles.mapBtn}
                    className="btn-secondary"
                  >
                    Effacer
                  </button>
                </div>
                <span style={styles.hintText}>
                  {!isPolygonClosed 
                    ? "Cliquez sur la carte pour dessiner. Cliquez sur le premier point (ou 'Fermer Zone') quand vous avez fini."
                    : "Zone fermée. Vous pouvez faire glisser les points pour ajuster le tracé."}
                </span>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Coordonnées du Polygone (JSON)</label>
                <textarea
                  placeholder='Ex: [[2.352, 48.856], [2.368, 48.868], [2.380, 48.845], [2.352, 48.856]]'
                  value={newZoneCoords}
                  onChange={(e) => setNewZoneCoords(e.target.value)}
                  style={styles.textarea}
                  className="input-field"
                  rows={2}
                  required
                  readOnly={draftPoints.length > 0}
                />
              </div>

              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '14px', marginTop: '4px' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: '700', marginBottom: '10px', color: 'var(--primary)' }}>
                  Tarification Spécifique par Zone (Optionnel)
                </h4>
                <div style={styles.inputGroup}>
                  <label style={styles.miniLabel}>Prix Forfaitaire Unique de la Zone (€)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    placeholder="Laisser vide pour utiliser la grille par défaut"
                    value={specificPrice}
                    onChange={(e) => setSpecificPrice(e.target.value)}
                    style={styles.input}
                    className="input-field"
                  />
                </div>
              </div>

              <div style={styles.formActions}>
                <button 
                  type="button" 
                  onClick={() => {
                    resetDraft();
                    setEditingZoneId(null);
                    setNewZoneName('');
                    setNewZoneCoords('');
                    setZoneStatus('PREPARATION');
                    setSpecificPrice('');
                    setFormOpen(false);
                  }} 
                  style={styles.cancelBtn}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={actionLoading}
                  style={styles.submitBtn}
                  className="btn-primary"
                >
                  {editingZoneId ? "Enregistrer" : "Ajouter la zone"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Audit Logs History Modal */}
      {historyOpen && selectedZone && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Historique d'activation - {selectedZone.name}</h3>
              <button onClick={() => setHistoryOpen(false)} style={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                Chargement de l'historique...
              </div>
            ) : auditLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                Aucune entrée d'historique pour cette zone.
              </div>
            ) : (
              <div style={styles.historyList}>
                {auditLogs.map((log) => (
                  <div key={log.id} style={styles.historyItem}>
                    <div style={styles.historyMeta}>
                      <span>Par: {log.admin ? log.admin.email : 'Système'}</span>
                      <span>{new Date(log.createdAt).toLocaleString('fr-FR')}</span>
                    </div>
                    <span style={styles.historyAction}>{log.action.replace('zone.', '').toUpperCase()}</span>
                    <p style={styles.historyDesc}>{log.metadata}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    animation: 'fadeIn 0.4s ease',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
    fontSize: '1.1rem',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--text-secondary)',
  },
  addBtn: {
    padding: '10px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  mainGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
  },
  tablePanel: {
    flex: 2,
    minWidth: '350px',
    overflowX: 'auto',
  },
  emptyTable: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
  },
  zoneName: {
    fontWeight: '600',
    fontFamily: 'var(--font-title)',
  },
  coordsCell: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    fontFamily: 'monospace',
  },
  toggleBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  formPanel: {
    flex: 1.2,
    minWidth: '320px',
    padding: '24px',
    alignSelf: 'flex-start',
    animation: 'fadeIn 0.3s ease',
  },
  formHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px',
  },
  formTitle: {
    fontSize: '1rem',
    fontWeight: '600',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
  },
  miniLabel: {
    fontSize: '0.7rem',
    fontWeight: '600',
    color: 'var(--text-muted)',
  },
  input: {
    fontSize: '0.85rem',
    padding: '8px 12px',
  },
  select: {
    fontSize: '0.85rem',
    padding: '8px 12px',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
  },
  textarea: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    lineHeight: '1.4',
    resize: 'none',
  },
  hintText: {
    fontSize: '0.7rem',
    color: 'var(--text-muted)',
    lineHeight: '1.4',
  },
  mapControls: {
    display: 'flex',
    gap: '8px',
    marginTop: '6px',
  },
  mapBtn: {
    flex: 1,
    padding: '6px 12px',
    fontSize: '0.75rem',
    borderRadius: 'var(--radius-sm)',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '1px solid var(--border-color)',
    paddingTop: '16px',
    marginTop: '8px',
  },
  cancelBtn: {
    padding: '8px 16px',
  },
  submitBtn: {
    padding: '8px 16px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    width: '500px',
    maxWidth: '90%',
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    padding: '24px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--shadow-main)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '12px',
    marginBottom: '16px',
  },
  modalTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  },
  historyList: {
    overflowY: 'auto',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    paddingRight: '4px',
  },
  historyItem: {
    padding: '12px',
    backgroundColor: 'var(--bg-tertiary)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-color)',
  },
  historyMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    marginBottom: '6px',
  },
  historyAction: {
    fontSize: '0.8rem',
    fontWeight: '700',
    color: 'var(--primary)',
  },
  historyDesc: {
    fontSize: '0.8rem',
    color: 'var(--text-primary)',
    lineHeight: '1.4',
  },
};
